import Tabs from '@orn/shared-web/components/Tabs';
import { useLocation, useSearchParams } from 'react-router-dom';

import Entity from '../../components/entity';
import BillingSettings from '../../components/settings/BillingSettings';
import DependentSettingsForm from '../../components/settings/DependentSettingsForm';
import FamilyInformationForm from '../../components/settings/FamilyInformationForm';
import NotificationSettingsForm from '../../components/settings/NotificationSettingsForm';
import PersonalSettingsForm from '../../components/settings/PersonalSettingsForm';
import PossessionsTab from '../../components/settings/PossessionsTab';
import { useDependents } from '../../hooks/queries/useDependents';
import { useViewerCanManageBilling } from '../../hooks/queries/useMembershipAccess';
import { displayName } from '../../lib/dependents/helpers';

import { SETTINGS_TAB_PARAM, SettingsTab, resolveSettingsTab, type SettingsTabKey } from './settingsTabs';

/** Tab label from the dependents' display names, alphabetically (e.g. "Bob & Linda"). */
const buildDependentsTabLabel = (names: string[]): string =>
  names
    .slice()
    .sort((a, b) => a.localeCompare(b))
    .join(' & ');

/**
 * Settings pane, rendered as a tabbed layout (the Mantle design, node
 * `4198:95792`): a "My account" tab with the Personal Information form, a
 * per-household "loved ones" tab (named after the dependents) with the
 * dependents form, a "Notifications" tab with the family-wide alert
 * thresholds, a "Possessions" tab with the family's vehicles, and a "Billing"
 * tab (a free-trial placeholder shown while billing collection is not yet
 * live). The Billing tab is a caregiver's only: a loved one's role is denied
 * every billing read, so it is left out — and its subscription read never
 * issued — unless `Family.viewerCanManageBilling` is `true`.
 *
 * The active tab is mirrored to the `?tab=` query param, so a tab is
 * deep-linkable and survives a refresh; bare `/settings` opens "My account".
 */
const SettingsPane = () => {
  const { dependents } = useDependents();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const showDependentsTab = dependents.length > 0;
  const showBillingTab = useViewerCanManageBilling() === true;
  const dependentsLabel = buildDependentsTabLabel(dependents.map(displayName));

  const availableTabs: SettingsTabKey[] = [
    SettingsTab.MyAccount,
    ...(showDependentsTab ? [SettingsTab.Dependents] : []),
    SettingsTab.Notifications,
    SettingsTab.Possessions,
    ...(showBillingTab ? [SettingsTab.Billing] : []),
  ];
  const selectedTab = resolveSettingsTab(searchParams.get(SETTINGS_TAB_PARAM), availableTabs);

  // Tabs.Root wraps the whole pane so the tab list (rendered in the header) and
  // the tab panels (in the scrollable body) share one react-aria selection
  // context. Keeping the list in the header pins it with the title and places
  // it above the scroll-driven drop shadow instead of fighting it.
  return (
    <Tabs.Root
      selectedKey={selectedTab}
      onSelectionChange={(key) =>
        // A tab is view state, not a destination — replace so Back exits Settings
        // rather than cycling through tabs, while the param still survives a refresh.
        setSearchParams(
          (params) => {
            params.set(SETTINGS_TAB_PARAM, String(key));
            return params;
          },
          // The entry's state rides along, so a sheet open over the page survives.
          { replace: true, state: location.state }
        )
      }
      className="h-full min-h-0 w-full"
    >
      <Entity.Pane>
        <Entity.Header
          title="Settings"
          belowContent={
            <Tabs.List aria-label="Settings sections">
              <Tabs.Tab id={SettingsTab.MyAccount}>My account</Tabs.Tab>
              {showDependentsTab && <Tabs.Tab id={SettingsTab.Dependents}>{dependentsLabel}</Tabs.Tab>}
              <Tabs.Tab id={SettingsTab.Notifications}>Notifications</Tabs.Tab>
              <Tabs.Tab id={SettingsTab.Possessions}>Possessions</Tabs.Tab>
              {showBillingTab && <Tabs.Tab id={SettingsTab.Billing}>Billing</Tabs.Tab>}
            </Tabs.List>
          }
        />
        <Entity.Body
          primaryElement={
            <>
              <Tabs.Panel id={SettingsTab.MyAccount} className="flex flex-col gap-5 pt-4 sm:pt-6">
                <FamilyInformationForm />
                <PersonalSettingsForm />
              </Tabs.Panel>
              {showDependentsTab && (
                <Tabs.Panel id={SettingsTab.Dependents} className="pt-4 sm:pt-6">
                  <DependentSettingsForm />
                </Tabs.Panel>
              )}
              <Tabs.Panel id={SettingsTab.Notifications} className="pt-4 sm:pt-6">
                <NotificationSettingsForm />
              </Tabs.Panel>
              <Tabs.Panel id={SettingsTab.Possessions} className="pt-4 sm:pt-6">
                <PossessionsTab />
              </Tabs.Panel>
              {showBillingTab && (
                <Tabs.Panel id={SettingsTab.Billing} className="pt-4 sm:pt-6">
                  <BillingSettings />
                </Tabs.Panel>
              )}
            </>
          }
        />
      </Entity.Pane>
    </Tabs.Root>
  );
};

export default SettingsPane;
